// ONDEMAND-1 (docs/specs/ondemand-1.md): TanStack Query hooks for transcribe-on-demand. Kept
// out of lib/queries.ts so that module doesn't keep growing. Same conventions: each hook falls
// back to in-memory fixtures (lib/mockOndemand.ts) when isMock() is true.
//
// Everything here except `useTranscriptionSettings` hits an endpoint that 404s unless the
// backend has the feature enabled — callers gate every other hook on `useOnDemandEnabled()`
// (via the `enabled` params, or by not rendering the component that calls a mutation).
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useApi } from './ApiContext';
import { isMock } from './mock';
import { mockOndemand } from './mockOndemand';
import type {
  BatchTranscribeResponse,
  CreateTranscriptionSubscriptionRequest,
  TranscriptionPolicy,
  TranscriptionSettings,
  TranscriptionStatus,
  TranscriptionSubscription,
  TranscriptionSubscriptionList,
  UntranscribedSearchResult,
} from '@scribe/core/types';

const SETTINGS_KEY = ['transcription-settings'] as const;
const SUBSCRIPTIONS_KEY = ['transcription-subscriptions'] as const;

function statusKey(recordingId: string | undefined) {
  return ['recording-transcription', recordingId] as const;
}

/** The feature flag + the caller's default policy. The ONE on-demand request made when the
 * feature is off. Settings change rarely, so a long staleTime keeps it to ~one call a session.
 * An error (e.g. an older backend without the endpoint) reads as "feature off". */
export function useTranscriptionSettings() {
  const { client } = useApi();
  return useQuery<TranscriptionSettings>({
    queryKey: SETTINGS_KEY,
    queryFn: () => (isMock() ? mockOndemand.getSettings() : client.getTranscriptionSettings()),
    staleTime: 5 * 60_000,
  });
}

/** `true` only once settings have loaded with `feature_enabled: true` — loading/error/off all
 * render exactly as before the feature existed. */
export function useOnDemandEnabled(): boolean {
  return useTranscriptionSettings().data?.feature_enabled === true;
}

export function useUpdateTranscriptionSettings() {
  const { client } = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userDefaultPolicy: TranscriptionPolicy | null) => {
      const body = { user_default_policy: userDefaultPolicy };
      return isMock()
        ? mockOndemand.updateSettings(body)
        : client.updateTranscriptionSettings(body);
    },
    onSuccess: (settings) => {
      qc.setQueryData(SETTINGS_KEY, settings);
      // The default feeds every recording's "why will/won't this auto-transcribe" line.
      void qc.invalidateQueries({ queryKey: ['recording-transcription'] });
    },
  });
}

/** Side-effect free: where a recording's transcription/summary stand, and why it will or
 * won't auto-transcribe. Shares its cache entry with `useEnsureTranscription`'s result. */
export function useRecordingTranscriptionStatus(recordingId: string | undefined, enabled: boolean) {
  const { client } = useApi();
  return useQuery<TranscriptionStatus>({
    queryKey: statusKey(recordingId),
    queryFn: () =>
      isMock()
        ? mockOndemand.getStatus(recordingId!)
        : client.getRecordingTranscriptionStatus(recordingId!),
    enabled: enabled && !!recordingId,
    staleTime: 30_000,
  });
}

/** After anything that may have started a transcription or summary: the recording row, the
 * list, and the summary artifacts are all stale. */
function invalidateRecording(qc: QueryClient, recordingId: string) {
  void qc.invalidateQueries({ queryKey: ['recording', recordingId] });
  void qc.invalidateQueries({ queryKey: ['recordings'] });
  void qc.invalidateQueries({ queryKey: ['recording-artifacts', recordingId] });
  void qc.invalidateQueries({ queryKey: ['transcript-versions', recordingId] });
  void qc.invalidateQueries({ queryKey: ['transcript', recordingId] });
}

/** Idempotent "someone wants this recording": starts the transcription of a deferred
 * recording, or the summary of a transcribed one that lacks it. A budget block comes back as
 * `state: 'budget_blocked'` (200), not as an error. */
export function useEnsureTranscription() {
  const { client } = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      recordingId,
      trigger = 'opened',
    }: {
      recordingId: string;
      trigger?: 'opened' | 'searched' | 'assistant';
    }) =>
      isMock()
        ? mockOndemand.ensure(recordingId, trigger)
        : client.ensureRecordingTranscription(recordingId, { trigger }),
    onSuccess: (status, vars) => {
      qc.setQueryData(statusKey(vars.recordingId), status);
      if (status.triggered || status.summary_triggered) invalidateRecording(qc, vars.recordingId);
    },
  });
}

/** Per-recording override (`null` = follow the default). Editor+ server-side. */
export function useSetRecordingTranscriptionPolicy(recordingId: string) {
  const { client } = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (policy: TranscriptionPolicy | null) =>
      isMock()
        ? mockOndemand.setPolicy(recordingId, policy)
        : client.setRecordingTranscriptionPolicy(recordingId, policy),
    onSuccess: (status) => {
      qc.setQueryData(statusKey(recordingId), status);
      // Setting `immediate` on a deferred recording may start it right away.
      invalidateRecording(qc, recordingId);
    },
  });
}

/** Deferred recordings matching a search, with a cost preview. Read-only — search NEVER
 * triggers a transcription by itself; only `useTranscribeBatch` (an explicit click) does. */
export function useUntranscribedSearch(q: string, enabled: boolean) {
  const { client } = useApi();
  const trimmed = q.trim();
  return useQuery<UntranscribedSearchResult>({
    queryKey: ['search-untranscribed', trimmed],
    queryFn: () =>
      isMock()
        ? mockOndemand.searchUntranscribed(trimmed)
        : client.searchUntranscribedRecordings({ q: trimmed }),
    enabled: enabled && trimmed.length > 0,
    placeholderData: (previous) => previous,
  });
}

/** Search's "Transcribe these" — at most 10 ids (the server rejects more). */
export function useTranscribeBatch() {
  const { client } = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (recordingIds: string[]): Promise<BatchTranscribeResponse> =>
      isMock()
        ? mockOndemand.transcribeBatch(recordingIds)
        : client.transcribeRecordingsBatch({ recording_ids: recordingIds, trigger: 'searched' }),
    onSuccess: (_result, recordingIds) => {
      for (const id of recordingIds) {
        void qc.invalidateQueries({ queryKey: ['recording', id] });
        void qc.invalidateQueries({ queryKey: statusKey(id) });
      }
      void qc.invalidateQueries({ queryKey: ['recordings'] });
      void qc.invalidateQueries({ queryKey: ['search-untranscribed'] });
    },
  });
}

export function useTranscriptionSubscriptions(enabled: boolean) {
  const { client } = useApi();
  return useQuery<TranscriptionSubscriptionList>({
    queryKey: SUBSCRIPTIONS_KEY,
    queryFn: () =>
      isMock() ? mockOndemand.listSubscriptions() : client.listTranscriptionSubscriptions(),
    enabled,
  });
}

function useSubscriptionMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SUBSCRIPTIONS_KEY });
      // A rule can change any recording's "why" line.
      void qc.invalidateQueries({ queryKey: ['recording-transcription'] });
    },
  });
}

export function useCreateTranscriptionSubscription() {
  const { client } = useApi();
  return useSubscriptionMutation(
    async (body: CreateTranscriptionSubscriptionRequest): Promise<TranscriptionSubscription> =>
      isMock()
        ? mockOndemand.createSubscription(body)
        : client.createTranscriptionSubscription(body),
  );
}

export function useUpdateTranscriptionSubscription() {
  const { client } = useApi();
  return useSubscriptionMutation(
    async ({
      id,
      isActive,
    }: {
      id: string;
      isActive: boolean;
    }): Promise<TranscriptionSubscription> =>
      isMock()
        ? mockOndemand.updateSubscription(id, isActive)
        : client.updateTranscriptionSubscription(id, isActive),
  );
}

export function useDeleteTranscriptionSubscription() {
  const { client } = useApi();
  return useSubscriptionMutation(async (id: string): Promise<void> =>
    isMock() ? mockOndemand.deleteSubscription(id) : client.deleteTranscriptionSubscription(id),
  );
}
